/**
 * FacilityContext.tsx — Contexto de facility (unidade/hospital) ativa no Órbita.
 *
 * Analogia ao EntityContext do ALADDIN (ADR-002 §3, UX.md §3).
 * Ao trocar de facility: dispara queryClient.clear() para invalidar cache (ADR-002 §4).
 *
 * activeHospitalId null = visão consolidada (Visão Executiva, Estrutura).
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { useTenant } from "./TenantContext";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface FacilityContextValue {
  /** ID da facility ativa (null = visão consolidada) */
  activeFacilityId: string | null;
  /** Troca a facility ativa; dispara cache clear */
  setActiveFacility: (facilityId: string | null) => void;
}

// ---------------------------------------------------------------------------
// Contexto
// ---------------------------------------------------------------------------

const FacilityContext = createContext<FacilityContextValue | null>(null);

export function FacilityProvider({ children }: { children: ReactNode }) {
  const { allowedFacilities } = useTenant();
  // Facility padrão: null (consolidado). Quando o tenant tiver facilities configuradas,
  // pode-se inicializar com allowedFacilities[0] após carregamento.
  const [activeFacilityId, setActiveFacilityIdState] = useState<string | null>(
    null,
  );

  const setActiveFacility = useCallback(
    (facilityId: string | null) => {
      // Verifica se a facility solicitada está na lista permitida
      if (
        facilityId !== null &&
        allowedFacilities.length > 0 &&
        !allowedFacilities.includes(facilityId)
      ) {
        return; // Ignora facilityId fora do escopo permitido
      }
      setActiveFacilityIdState(facilityId);
      // TODO: quando TanStack Query estiver integrado, chamar queryClient.clear() aqui
      // para invalidar cache ao trocar de entidade (ADR-002 §4)
    },
    [allowedFacilities],
  );

  return (
    <FacilityContext.Provider value={{ activeFacilityId, setActiveFacility }}>
      {children}
    </FacilityContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

// eslint-disable-next-line react-refresh/only-export-components -- padrão context: provider + hook no mesmo arquivo
export function useFacility(): FacilityContextValue {
  const ctx = useContext(FacilityContext);
  if (!ctx) {
    throw new Error("useFacility deve ser usado dentro de <FacilityProvider>.");
  }
  return ctx;
}
